/**
 * The bulk disposition (D-28) and Needs verification (D-29), Dominique Genua
 * 2026-10-02 (April to confirm). Every type mirrors what
 * `routes/manifestResubmittals.js`, `services/advocate/bulkDisposition.js` and
 * `services/advocate/verifications.js` return.
 *
 * Its own file on purpose: manifestApi.ts is another builder's surface.
 */
import { apiRequest } from '../../services/api';
import type { DispositionWord } from './resubmittalsApi';

const BASE = '/manifest';

/* ── D-28: the bulk disposition ─────────────────────────────────────────── */

/** The server takes at most 500 per call; the screen sends this many at a time and shows progress. */
export const BULK_BATCH = 200;
/** What the action bar offers first: Dominique's "submit them all at once" to the provider. */
export const DEFAULT_BULK_WORD: DispositionWord = 'RESUBMIT TO PROVIDER';

/** Why the server left a case alone. Every code has words below; a code the screen does not know is shown as itself. */
export type SkipCode =
  | 'NOT_FOUND' | 'ALREADY_CLOSED' | 'CARRIED' | 'RESIDENT_HELD' | 'REPLACEMENT_ROW' | 'NOT_A_PLACEMENT_ROW'
  | 'NO_RESIDENT' | 'ALREADY_PENDING' | 'ALREADY_IN_VERIFICATION' | 'TRANSITION_INVALID' | 'FAILED';

export const SKIP_WORDS: Record<SkipCode, string> = {
  NOT_FOUND: 'no longer there, or outside your buildings',
  ALREADY_CLOSED: 'already closed',
  CARRIED: 'closed together with the same resident\'s other record',
  RESIDENT_HELD: 'the resident already has a Patient Advocate',
  REPLACEMENT_ROW: 'a replacement (worked from the Replacements desk)',
  NOT_A_PLACEMENT_ROW: 'already past the UMED handoff',
  NO_RESIDENT: 'not linked to a resident',
  ALREADY_PENDING: 'already on Resubmittals',
  ALREADY_IN_VERIFICATION: 'a VA is already verifying them',
  TRANSITION_INVALID: 'cannot take that status from where it is',
  FAILED: 'eMerge failed on it; nothing was changed for it',
};
export const skipWords = (code: string): string => SKIP_WORDS[code as SkipCode] || code;

export type AgeBands = { over_1_year: number; six_to_12_months: number; days_45_to_6_months: number; under_45_days: number; unknown: number };

export type BulkPreview = {
  word: DispositionWord;
  requested: number;
  would: string[];
  excluded: Array<{ case_id: string; code: SkipCode }>;
  excluded_counts: Partial<Record<SkipCode, number>>;
  /** By the date the case was last placed, else opened. */
  age_bands: AgeBands;
};

export type BulkOutcome = {
  batch_id: string;
  word: DispositionWord;
  requested: number;
  done: string[];
  skipped: Array<{ case_id: string; code: SkipCode }>;
  skipped_counts: Partial<Record<SkipCode, number>>;
  /** True when this key was already recorded: the answer is the recorded one, nothing was done twice. */
  replayed: boolean;
};

export type BulkSelection = { case_ids: string[]; total: number; older_than_days: number | null };

/* ── D-29: Needs verification ───────────────────────────────────────────── */

export type VerificationRequest = {
  id: string;
  patient_id: string;
  originating_case_id: string | null;
  /** What the VA is asked to check. */
  note: string;
  requested_at: string;
  prior_word: string | null;
  facility_id: string | null;
  facility_name: string | null;
  /** CounselEAR's Preferred Clinic, sent when eMerge has no building (2026-10-14). Shown as "CounselEAR clinic: X (building not confirmed)". */
  counselear_clinic_id?: string | null;
  counselear_clinic_name?: string | null;
  first_name: string | null;
  last_name: string | null;
  /** YYYY-MM-DD: a day, not an instant. */
  dob: string | null;
  medicaid_number: string | null;
  counselear_id: string | null;
  requested_by_name: string | null;
  prior_holder_user_id: string | null;
  /** The PA who held the case; "Qualifies" places it back with them. Null: it goes to Leads to place. */
  prior_holder_name: string | null;
  name_received: boolean;
};

export type VerificationGroup = { facility_id: string | null; facility_name: string | null; items: VerificationRequest[] };

/** A verified resident whose case reopened unassigned: Leads to place lists them, tagged "Verified — qualifies". */
export type VerifiedLead = {
  verification_id: string;
  patient_id: string;
  case_id: string;
  verified_at: string;
  verified_note: string;
  verified_by_name: string | null;
  first_name: string | null;
  last_name: string | null;
  facility_id: string | null;
  facility_name: string | null;
  /** CounselEAR's Preferred Clinic, sent when eMerge has no building (2026-10-14). Shown as "CounselEAR clinic: X (building not confirmed)". */
  counselear_clinic_id?: string | null;
  counselear_clinic_name?: string | null;
  twl_tag_since: string | null;
  open_cases: number;
  suggested_advocate_id: string | null;
  suggested_advocate: string | null;
  coverage_reason: string | null;
  coverage_source: 'HISTORY' | 'MANAGER' | null;
  coverage_confirmed: boolean;
  verified_qualifies: true;
  workable: true;
  recorded: false;
  counselear_lead: false;
  intake_exceptions: null;
};

export type VerificationList = {
  groups: VerificationGroup[];
  total: number;
  returned: VerifiedLead[];
  /** May this caller answer? The server decides again on every call. */
  can_act: boolean;
};

/** Where a qualified resident went. */
export type Reentry = 'PRIOR_HOLDER' | 'LEADS_TO_PLACE' | 'ALREADY_OPEN';

export type VerificationAnswer = {
  id: string;
  state: 'QUALIFIED' | 'NOT_QUALIFIED';
  reentry: Reentry | null;
  case_id: string | null;
  replayed: boolean;
  word?: string | null;
  holder_user_id?: string | null;
};

export const verificationApi = {
  /* D-28 */
  /** The ids an Unassigned filter shows (one per resident), optionally only those last placed or opened more than N days ago. */
  selection: (body: { q?: string | null; lens?: string | null; reason?: string | null; older_than_days?: number | null }) =>
    apiRequest<BulkSelection>('POST', `${BASE}/bulk-disposition/selection`, body),
  /** Writes nothing. At most 500 ids. */
  preview: (caseIds: string[], status: DispositionWord) =>
    apiRequest<BulkPreview>('POST', `${BASE}/bulk-disposition/preview`, { case_ids: caseIds, status }),
  /** At most 500 ids; one key per batch (letters, digits, . _ : -, at most 64). */
  run: (caseIds: string[], status: DispositionWord, reason: string, idempotencyKey: string) =>
    apiRequest<BulkOutcome>('POST', `${BASE}/bulk-disposition`, { case_ids: caseIds, status, reason, idempotency_key: idempotencyKey }),

  /* D-29 */
  list: () => apiRequest<VerificationList>('GET', `${BASE}/verifications`),
  /** How many OPEN requests the list would show: the number on the tab. A count only. */
  count: () => apiRequest<{ open: number }>('GET', `${BASE}/verifications/count`),
  qualifies: (id: string, note: string) => apiRequest<VerificationAnswer>('POST', `${BASE}/verifications/${id}/qualifies`, { note }),
  doesNotQualify: (id: string, note: string) => apiRequest<VerificationAnswer>('POST', `${BASE}/verifications/${id}/does-not-qualify`, { note }),
};

/**
 * The case reference an email about this request carries (`PA-1A2B3C4D`: the originating Patient Advocate case's
 * first eight hex digits, services/caseReference.js). Typed into the list's Find box, it finds the row. Null when
 * the request has no case.
 */
export function caseReferenceOf(r: { originating_case_id: string | null }): string | null {
  const id = r.originating_case_id;
  return id && /^[0-9a-f]{8}-/i.test(id) ? `PA-${id.slice(0, 8).toUpperCase()}` : null;
}

/** Split ids into the batches the screen sends. */
export function batches<T>(xs: T[], size = BULK_BATCH): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < xs.length; i += size) out.push(xs.slice(i, i + size));
  return out;
}
